import { FC, useLayoutEffect, useRef, useState, PropsWithChildren, memo } from 'react'
import cn from 'classnames'
import PoapImage from 'components/PoapImage'
import Status from './Status'

import { GpsSupplyRequest } from './types/types'

import ImageModal from 'backoffice/components/DropRequests/Modals/ImageModal'
import GpsPetitionMetadata from './GpsPetitionMetadata'
import { formatDate, formatDuration, DATE_FORMAT_MASK_WITH_TIME } from 'lib/date'
import { hasEllipsis } from 'lib/dom'

import { ReactComponent as CalendarIcon } from 'images/icons/calendar.svg'
import { ReactComponent as ClockIcon } from 'images/icons/clock.svg'
import { ReactComponent as DescriptionIcon } from 'images/icons/description.svg'
import { ReactComponent as ReviewerIcon } from 'images/icons/secure-user.svg'
import { ReactComponent as GlobeIcon } from 'images/icons/globe.svg'
import { ReactComponent as UserIcon } from 'images/icons/secure-user.svg'

import styles from './Card.module.scss'

const GpsDescription: FC<PropsWithChildren<{}>> = ({ children }) => {
  const containertEl = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState({ isCollapsed: true, hasEllipsis: false })

  const handleDescription = () => setOverflow((prev) => ({ ...prev, isCollapsed: !prev.isCollapsed }))

  useLayoutEffect(() => {
    const el = containertEl.current
    if (el) {
      setOverflow((prev) => ({ ...prev, hasEllipsis: hasEllipsis(el) }))
    }
  }, [])

  return (
    <>
      <div ref={containertEl} className={cn(overflow.isCollapsed && styles.descriptionHidden)}>
        {children}
      </div>
      {overflow.hasEllipsis && (
        <button type="button" onClick={handleDescription} className={styles.toggleButton}>
          {overflow.isCollapsed ? 'More' : 'Less'}
        </button>
      )}
    </>
  )
}

const EventImage: FC<{ image: string }> = ({ image }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const handleClose = () => setIsOpen(false)
  const handleClick = () => setIsOpen(true)

  return (
    <>
      <PoapImage
        className={styles.image}
        src={image}
        loading="lazy"
        alt="Event"
        width="104"
        height="104"
        onClick={handleClick}
      />

      <ImageModal imageUrl={image} isOpen={isOpen} onCloseClick={handleClose} />
    </>
  )
}

type GpsPetitionCardProps = {
  petition: GpsSupplyRequest
}

const GpsPetitionCard: FC<GpsPetitionCardProps> = ({ petition }) => {
  const { drop } = petition

  return (
    <div className={styles.card}>
      {drop && <EventImage image={drop.imageUrl} />}

      <div className={styles.info}>
        {drop && (
          <div className={styles.infoItem}>
            <CalendarIcon className={styles.icon} />
            <div aria-label="Drop Duration" data-cooltipz-dir="top">
              <span className={styles.date}>{formatDate(drop.startDate)}</span>
              {' to '}
              <span className={styles.date}>{formatDate(drop.endDate)}</span>
            </div>
            <div className={styles.externalLinks}>
              <a
                href={`${process.env.REACT_APP_GALLERY_DOMAIN}/event/${drop.id}`}
                target="_blank"
                className={styles.link}
              >
                Gallery
              </a>
              <a
                href={`${process.env.REACT_APP_FAMILY_DOMAIN}/event/${drop.id}`}
                target="_blank"
                className={styles.link}
              >
                Family
              </a>
            </div>
          </div>
        )}

        {drop?.issuerEmail && (
          <div className={styles.infoItem}>
            <UserIcon className={styles.icon} />
            <div aria-label="Issuer Email" data-cooltipz-dir="top">
              <span className={styles.issuerEmail}>{drop.issuerEmail}</span>
            </div>
          </div>
        )}

        {drop?.eventUrl && (
          <div className={styles.infoItem}>
            <GlobeIcon className={styles.icon} />
            <a href={drop.eventUrl} target="_blank" className={styles.dropUrl}>
              {drop.eventUrl}
            </a>
          </div>
        )}

        {drop?.description && (
          <div className={styles.infoItem}>
            <DescriptionIcon className={styles.icon} />
            <div aria-label="Description" data-cooltipz-dir="top">
              <GpsDescription>{drop.description}</GpsDescription>
            </div>
          </div>
        )}

        <GpsPetitionMetadata petition={petition} />
      </div>

      <hr className={styles.hr} />

      <div className={styles.footer}>
        <div className={styles.reviewInfo}>
          <ReviewerIcon width={18} height={18} className={styles.reviewerIcon} />
          <Status petition={petition} dropName={drop?.name} />
        </div>
      </div>
    </div>
  )
}

export default memo(GpsPetitionCard)
